<template>
    <TprmModal
        v-if="isImportMode"
        additional-class-name="BulkImportEntitiesModal"
        size="lg"
        :allow-close="!saving"
        open
        title="Bulk Import Entities"
        @close="close"
    >
        <Alert
            v-if="droppedColumnsAlertConfig"
            :type="droppedColumnsAlertConfig.type"
            :title="droppedColumnsAlertConfig.title"
        />
        <Alert
            v-if="producersNoticeAlertConfig"
            :type="producersNoticeAlertConfig.type"
            :title="producersNoticeAlertConfig.title"
        />
        <form @submit.prevent="submit">
            <BulkImportForm
                :disabled="saving || processing"
                :value="files"
                :custom-properties="sortedCustomProperties"
                :requirement-types="requirementTypes"
                :alert-config="alertConfig"
                :build-description="buildFileDescription"
                :contact-email-uniqueness-enabled="contactEmailUniquenessEnabled"
                @input="processFile"
            />
        </form>

        <template #footer>
            <NewButton
                class="BulkImportEntitiesModal__submitButton"
                :disabled="processing || files.length === 0 || isInvalidFile"
                :loading="saving"
                :progress="savingPercentage"
                color="primary"
                variant="fill"
                @click="submit"
            >
                Import
            </NewButton>
        </template>
    </TprmModal>
    <TprmModal
        v-else
        additional-class-name="BulkImportEntitiesCorrectionModal"
        title="Import results"
        size="full-screen"
        open
        :allow-close="!saving"
        @close="close"
    >
        <Alert
            v-if="droppedColumnsAlertConfig"
            :type="droppedColumnsAlertConfig.type"
            :title="droppedColumnsAlertConfig.title"
        />
        <Alert
            v-if="producersNoticeAlertConfig"
            :type="producersNoticeAlertConfig.type"
            :title="producersNoticeAlertConfig.title"
        />
        <form @submit.prevent="submit">
            <BulkImportCorrectionTable
                v-model="corrections"
                :form="form"
                :additional-alert-config="correctionTableSuccessAlertConfig"
                :csv-column-keys="csvColumnKeys"
            />
        </form>

        <template #footer>
            <NewButton
                class="BulkImportEntitiesCorrectionModal__csvButton"
                :disabled="!valid || saving"
                color="secondary"
                variant="outline"
                @click="exportCsv"
            >
                <FontAwesomeIcon :icon="faFileExcel" />
                Download updated CSV worksheet
            </NewButton>
            <NewButton
                class="BulkImportEntitiesCorrectionModal__importButton"
                :loading="saving"
                :progress="savingPercentage"
                :disabled="!valid"
                color="primary"
                @click="submit"
            >
                Save and Import
            </NewButton>
        </template>
    </TprmModal>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { zipObject } from 'lodash';
    import fromPairs from 'lodash/fromPairs';
    import isPlainObject from 'lodash/isPlainObject';
    import partition from 'lodash/partition';
    import trim from 'lodash/trim';
    import uniq from 'lodash/uniq';
    import moment from 'moment';

    import { faFileExcel } from '@fortawesome/free-regular-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { Alert } from '@evidentid/dashboard-commons/components/Alert';
    import { AlertConfig, AlertType } from '@evidentid/dashboard-commons/components/Alert/types';
    import { downloadFileUsingBinString } from '@evidentid/file-utils/blobs';
    import createForm from '@evidentid/json-schema/createForm';
    import {
        JsonFormObject,
        JsonFormProperty,
        JsonFormType,
    } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaArray, JsonSchemaObject, JsonSchemaType } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { isArray, isBoolean, isObject } from '@evidentid/json-schema/schemaChecks';
    import { CustomProperty, EntityInput } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { buildCsv, readCsv } from '@evidentid/universal-framework/csv';

    import LoadingModal from '@/components/loading-modal/LoadingModal.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import { removeNonStandardSchemaFormat } from '@/modules/decisioning-criteria/utils/removeNonStandardSchemaFormat';
    import {
        ENTITY_ID_COLUMN_LABEL,
    } from '@/modules/entity-management/constants/entityIdColumn.constant';
    import {
        LEGACY_CSV_FORMAT_MESSAGE,
        PRODUCERS_CUSTOM_PROPERTY_MESSAGE,
        PRODUCERS_EMAIL_NOTICE,
        PRODUCERS_FLOW_OFF_MESSAGE,
        PRODUCERS_WORKSHEET_MESSAGE,
    } from '@/modules/entity-management/constants/entityMessages.constant';
    import {
        PRODUCERS_COLUMN_LABEL,
    } from '@/modules/entity-management/constants/producersColumn.constant';
    import { BaseEntityInput } from '@/modules/entity-management/types';
    import {
        buildBulkEntityImportJsonSchema,
    } from '@/modules/entity-management/utils/build-entity-json-schema/buildEntityJsonSchema';
    import {
        getProducerCoverageTypeSchema,
    } from '@/modules/entity-management/utils/build-entity-json-schema/helpers';
    import { buildEntitiesCsv, buildEntitiesDownloadDataCsv } from '@/modules/entity-management/utils/buildEntityCsv';
    import { isSampleEntity } from '@/modules/entity-management/utils/csv-sample';
    import {
        filterEntityEmptyItems,
        hasPastExpirationDate,
        trimEntity,
    } from '@/modules/entity-management/utils/entityProcessing';
    import {
        getCsvColumnKeysWithCombinedObjects,
    } from '@/modules/entity-management/utils/get-csv-column-keys-with-combined-objects/getCsvColumnKeysWithCombinedObjects';
    import {
        getCsvValuesWithCombinedObjects,
    } from '@/modules/entity-management/utils/get-csv-values-with-combined-objects/getCsvValuesWithCombinedObjects';
    import {
        getFlattenedCustomPropertyKeys,
    } from '@/modules/entity-management/utils/get-flattened-custom-property-keys/getFlattenedCustomPropertyKeys';
    import {
        getFlattenedCustomPropertyNames,
    } from '@/modules/entity-management/utils/get-flattened-custom-property-names/getFlattenedCustomPropertyNames';
    import {
        getFlattenedStandardPropertyKeys,
    } from '@/modules/entity-management/utils/get-flattened-standard-property-keys/getFlattenedStandardPropertyKeys';
    import {
        getFlattenedStandardPropertyNames,
    } from '@/modules/entity-management/utils/get-flattened-standard-property-names/getFlattenedStandardPropertyNames';
    import { normalizeEntityId } from '@/modules/entity-management/utils/normalize-entity-id/normalizeEntityId';
    import {
        isEntityExportCsv,
    } from '@/modules/entity-management/utils/normalize-export-csv/isEntityExportCsv';
    import {
        buildExportLayoutRows,
        ExportLayoutSource,
    } from '@/modules/entity-management/utils/normalize-export-csv/buildExportLayoutRows';
    import {
        normalizeExportCsv,
    } from '@/modules/entity-management/utils/normalize-export-csv/normalizeExportCsv';
    import { hasProducersColumn } from '@/modules/entity-management/utils/producers/hasProducersColumn';
    import {
        withImportedProducers,
        withResolvedCoverageTypes,
    } from '@/modules/entity-management/utils/producers/importedProducers';
    import {
        collectProducerEmails,
        describeProducerProblems,
        findProducerProblems,
    } from '@/modules/entity-management/utils/producers/producerImportChecks';
    import { isCollateralCustomProperty } from '@/utils/isCollateralCustomProperty';
    import { isEquipmentCustomProperty } from '@/utils/isEquipmentCustomProperty';

    import BulkImportCorrectionTable from '../BulkImportCorrectionTable/BulkImportCorrectionTable.vue';
    import BulkImportForm from '../BulkImportForm/BulkImportForm.vue';

    import { buildValue } from './buildValue';

    function parseDateValue(_value: string | undefined): string | null {
        const momentDate = moment(trim(_value || ''));
        return momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : null;
    }

    function isPropertyEmpty(customPropertyValue: any) {
        return customPropertyValue !== null && typeof customPropertyValue === 'object' && Object.values(customPropertyValue).every((value) => value === '');
    }

    function nullifyEmptyObjectFields(
        properties: Record<string, string | boolean | object | object[] | string[] | null>,
    ) {
        return Object.entries(properties)
            .reduce((acc, [ key, customPropertyValue ]) => {
                acc[key] = isPropertyEmpty(customPropertyValue) ? null : customPropertyValue;
                return acc;
            }, {} as Record<string, any>);
    }

    function buildInsuredInput(input: any, form: JsonFormObject): BaseEntityInput {
        const base = form.getProperties();
        const customPropertiesFormProp = base.find((x) => x.name === 'insuredFields');
        const customPropertiesFormPropList = customPropertiesFormProp?.form?.type === JsonFormType.object
            ? customPropertiesFormProp.form.getProperties()
            : [];
        const exceptionsProperty = base.find((x) => x.name === 'exceptions');
        const exceptionProperties =
            exceptionsProperty?.form?.type === JsonFormType.object ? exceptionsProperty.form.getProperties() : [];
        const baseValues = fromPairs(base.map((x) => [ x.name, buildValue(input[x.name], x.form) ]));
        // `null` or `5` is valid JSON but not a producer, and getValue below would turn it into a blank one.
        if (Array.isArray(baseValues.producerList) && !baseValues.producerList.every(isPlainObject)) {
            throw new SyntaxError('Every producer must be a JSON object.');
        }
        // TODO[PRODUCT-23623]: band-aid for address conflict, needs support generic object type standard prop
        baseValues.address = input.entityAddress;
        const customProperties =
            fromPairs(customPropertiesFormPropList.map((x) => [ x.name, buildValue(input[x.name], x.form) ]));
        const exceptions =
            fromPairs(exceptionProperties.map((x) => [ x.name, parseDateValue(input[x.name]) || input[x.name] ]));
        const transformedCustomProperties = nullifyEmptyObjectFields(customProperties);
        const transformedBase = nullifyEmptyObjectFields(baseValues);
        return form.getValue({ ...transformedBase, insuredFields: transformedCustomProperties, exceptions }, true);
    }

    // PRODUCT-26110 QA fixes4: declares every event this component `$emit`s -- without a
    // declared `emits`, Vue 3 warns "Component emitted event 'close'/'input'/'submit' but
    // it is neither declared in the emits option nor as an 'onClose'/... prop" every time
    // it fires.
    @Component({
        emits: [ 'close', 'input', 'submit' ],
        components: {
            Alert,
            NewButton,
            TprmModal,
            FontAwesomeIcon,
            BulkImportForm,
            BulkImportCorrectionTable,
            LoadingModal,
        },
    })
    export default class BulkImportEntitiesModal extends Vue {
        @Prop({ type: Array, default: () => [] })
        private customProperties!: CustomProperty[];

        @Prop({ type: Array, default: () => [] })
        private requirementTypes!: string[];

        @Prop({ type: Object, default: null })
        private externalAlertConfig!: { type: AlertType, title: string } | null;

        @Prop({ type: Boolean, default: false })
        private saving!: boolean;

        @Prop({ type: Number, default: null })
        private savingPercentage!: number | null;

        // Whether the relying party still requires one entity per contactEmail (PRODUCT-26139).
        // Defaults to true: that is what every relying party did before the flag existed, and the
        // reading that keeps older worksheets importable if the config has not loaded yet.
        @Prop({ type: Boolean, default: true })
        private contactEmailUniquenessEnabled!: boolean;

        // Coverage types an imported producer may be given, or null when the relying party has no
        // producer flow, which then imports no producers.
        @Prop({ type: Array, default: null })
        private producerCoverageTypes!: string[] | null;

        // Resolves to the producer emails the API would refuse for a personal or disposable domain.
        @Prop({ type: Function, default: async () => [] })
        private findBlockedProducerEmails!: (emails: string[]) => Promise<string[]>;

        // Display label per coverage-type key, so a file may name a coverage type either way.
        @Prop({ type: Object, default: () => ({}) })
        private producerCoverageTypeLabels!: Record<string, string>;

        private files: File[] = [];
        private jsonSchema: JsonSchemaObject = null as any;
        private form: JsonFormObject = null as any;
        private fileDescription: { description?: string, error?: boolean } = {};
        private localAlertConfig: AlertConfig | null = null;
        private droppedColumns: string[] = [];
        private hasProducers: boolean = false;
        private fileHasProducersColumn: boolean = false;
        // The uploaded Download Data file as read, so the corrected file can be written in its layout.
        private exportSource: ExportLayoutSource | null = null;
        private correctionTableSuccessAlertConfig: { type: AlertType, title: string } | null = null;
        private processing: boolean = false;
        private entities: EntityInput[] = [];
        private corrections: BaseEntityInput[] = [];
        private sampleEntities: EntityInput[] = [];
        private faFileExcel = faFileExcel;
        private csvHeaders: string[] = [];
        private csvColumnKeys: string[] = [];

        @Watch('value')
        private onInternalValueChange(): void {
            this.$emit('input', this.value);
        }

        private get value(): EntityInput[] {
            return this.getValue(true);
        }

        private get isImportMode(): boolean {
            return this.corrections.length === 0 || this.files.length === 0 || Boolean(this.alertConfig);
        }

        private get isInvalidFile(): boolean {
            return Boolean(this.fileDescription && this.fileDescription.error);
        }

        private isValid(value: BaseEntityInput): boolean {
            return this.form.isValid(filterEntityEmptyItems(value, this.sortedCustomProperties), true);
        }

        private get standardProperties(): JsonFormProperty[] {
            return this.form.getProperties().filter((x) => x.name !== 'insuredFields' && x.name !== 'exceptions');
        }

        private get entityColumnsKeys(): string[] {
            return [
                ...getFlattenedStandardPropertyKeys(this.standardProperties),
                ...getFlattenedCustomPropertyKeys(this.sortedCustomProperties),
                ...this.requirementTypes,
            ];
        }

        private get entityColumnsLabel(): string[] {
            return [
                ...getFlattenedStandardPropertyNames(this.standardProperties),
                ...getFlattenedCustomPropertyNames(this.sortedCustomProperties),
                ...this.requirementColumnLabels,
            ];
        }

        private get entityColumnsLabelKeyMap(): Record<string, string> {
            return this.entityColumnsLabel.reduce((acc, label, index) => {
                acc[label] = this.entityColumnsKeys[index];
                return acc;
            }, {} as Record<string, string>);
        }

        private get requirementColumnLabels(): string[] {
            return this.requirementTypes.map((requirement) => `Existing ${requirement} Policy Expiration Date`);
        }

        // Canonical import labels of boolean custom properties. The export writes booleans
        // as "true"/"false"; normalizeExportCsv converts these columns to the import's Yes/No.
        // A scalar boolean's flattened label is its name (object/array booleans are skipped).
        private get booleanColumnLabels(): string[] {
            return this.sortedCustomProperties
                .filter((field) => isBoolean(field.schema))
                .flatMap((field) => getFlattenedCustomPropertyNames([ field ]));
        }

        // Canonical import labels of collateral custom properties. The export writes collaterals
        // as a single JSON-array string; normalizeExportCsv converts these columns to the
        // pipe-separated JSON-objects form the import expects.
        private get collateralColumnLabels(): string[] {
            return this.sortedCustomProperties
                .filter((field) => isCollateralCustomProperty(field))
                .flatMap((field) => getFlattenedCustomPropertyNames([ field ]));
        }

        // Canonical import labels of Equipment custom properties. The export writes Equipment
        // as a single JSON-array string, the same shape it uses for collaterals;
        // normalizeExportCsv converts these columns with the same transform.
        private get equipmentColumnLabels(): string[] {
            return this.sortedCustomProperties
                .filter((field) => isEquipmentCustomProperty(field))
                .flatMap((field) => getFlattenedCustomPropertyNames([ field ]));
        }

        // Canonical import labels of list (array-of-primitive) custom properties, e.g.
        // LIST_EMAIL and LIST_STRING. The export writes these as a single JSON-array string;
        // normalizeExportCsv converts these columns to the pipe-separated form the import's
        // buildValue expects. Array-of-object properties (collaterals and equipment) are handled separately.
        private get listColumnLabels(): string[] {
            return this.sortedCustomProperties
                .filter((field) => isArray(field.schema)
                    && (field.schema as JsonSchemaArray<any>).items.type !== JsonSchemaType.object)
                .flatMap((field) => getFlattenedCustomPropertyNames([ field ]));
        }

        private get valid(): boolean {
            return this.corrections.every((value) => this.isValid(value));
        }

        private get alertConfig(): AlertConfig | null {
            return this.files.length === 0 ? null : this.localAlertConfig || this.externalAlertConfig;
        }

        private get droppedColumnsAlertConfig(): AlertConfig | null {
            return this.droppedColumns.length > 0
                ? {
                    type: 'regular' as AlertType,
                    title: 'Some columns from your download are read-only and were not '
                        + `imported: ${this.droppedColumns.join(', ')}.`,
                }
                : null;
        }

        private get producersNoticeAlertConfig(): AlertConfig | null {
            return this.hasProducers ? { type: 'regular' as AlertType, title: PRODUCERS_EMAIL_NOTICE } : null;
        }

        /** A custom property named or keyed "Producers", whose column would clash with the built-in one. */
        private get hasProducersCustomProperty(): boolean {
            return [
                ...getFlattenedCustomPropertyNames(this.sortedCustomProperties),
                ...getFlattenedCustomPropertyKeys(this.sortedCustomProperties),
            ].includes(PRODUCERS_COLUMN_LABEL);
        }

        // Such a custom property owns the "Producers" column name, so producers stay out of the import there.
        private get importableProducerCoverageTypes(): string[] | null {
            return this.hasProducersCustomProperty ? null : this.producerCoverageTypes;
        }

        private get sortedCustomProperties(): CustomProperty[] {
            return this.customProperties;
        }

        private getValue(ignoreEmptyOptional: boolean): EntityInput[] {
            return [ ...this.entities, ...this.corrections as EntityInput[] ]
                .map((entity) => this.form.getValue(entity, ignoreEmptyOptional));
        }

        @Watch('customProperties', { immediate: true })
        @Watch('requirementTypes')
        @Watch('producerCoverageTypes')
        @Watch('producerCoverageTypeLabels')
        private onCustomPropertiesUpdate(): void {
            this.jsonSchema = buildBulkEntityImportJsonSchema(
                this.sortedCustomProperties,
                this.requirementTypes,
                this.importableProducerCoverageTypes,
                this.producerCoverageTypeLabels,
            );
            this.form = createForm(removeNonStandardSchemaFormat(this.jsonSchema) as JsonSchemaObject);
        }

        private close(): void {
            this.$emit('close');
        }

        private async processFile([ file ]: File[]): Promise<void> {
            this.files = file ? [ file ] : [];
            this.processing = true;
            this.fileDescription = {};
            this.localAlertConfig = null;
            this.droppedColumns = [];
            this.hasProducers = false;
            this.fileHasProducersColumn = false;
            this.exportSource = null;
            if (file) {
                try {
                    const { rows, csvHeaders, csvColumnKeys, droppedColumns } = await this.readInsuredInput(file);
                    this.csvHeaders = csvHeaders;
                    this.csvColumnKeys = csvColumnKeys;
                    this.droppedColumns = droppedColumns;
                    const [ sampleEntities, entities ] = partition(
                        rows,
                        (x) => isSampleEntity(x),
                    ) as ([ EntityInput[], BaseEntityInput[] ]);
                    // Sample rows are never imported, so only the rest are checked.
                    await this.checkProducers(entities);
                    this.hasProducers = entities.some((entity) => (entity.producerList || []).length > 0);
                    this.sampleEntities = sampleEntities;
                    [ this.entities, this.corrections ] =
                        partition(entities, (x) => this.isValid(x)) as [ EntityInput[], BaseEntityInput[] ];
                    this.fileDescription = {
                        description: `${rows.length} entities found`,
                        error: false,
                    };
                    const entityText =
                        this.entities.length > 1 ? 'Entities successfully validated' : 'Entity successfully validated';
                    this.correctionTableSuccessAlertConfig = this.entities.length > 0
                        ? { type: 'success', title: `${this.entities.length} ${entityText}` }
                        : null;
                } catch (error) {
                    this.localAlertConfig = {
                        title: 'Unable to import! Please fix the errors and upload the CSV file',
                        type: 'danger',
                    };
                    this.fileDescription = { description: (error as Error).message, error: true };
                }
            }
            this.processing = false;
        }

        private submit(): void {
            if (this.valid) {
                const trimAndFilter = (entity: EntityInput) => withImportedProducers(normalizeEntityId(
                    this.form.getValue(
                        filterEntityEmptyItems(trimEntity(entity), this.sortedCustomProperties),
                        true),
                    this.contactEmailUniquenessEnabled), this.fileHasProducersColumn);
                const hasPastDate = this.value.some(hasPastExpirationDate);
                this.$emit('submit', this.value.map(trimAndFilter), this.sampleEntities, hasPastDate);
            }
        }

        private exportCsv(): void {
            if (this.valid) {
                downloadFileUsingBinString(
                    buildCsv(this.correctedFileRows()),
                    this.files[0].name.replace(/\.csv$/, '_updated.csv'),
                );
            }
        }

        /** The corrected rows in the layout they were uploaded in, so the file imports again the same way. */
        private correctedFileRows(): string[][] {
            const buildRows = this.exportSource ? buildEntitiesDownloadDataCsv : buildEntitiesCsv;
            const rows = buildRows(
                this.csvHeaders, this.csvColumnKeys, this.getValue(false), this.sortedCustomProperties,
            );
            return this.exportSource
                ? buildExportLayoutRows(this.exportSource, rows, {
                    entityColumnsLabel: this.entityColumnsLabel,
                    keyToLabel: zipObject(this.entityColumnsKeys, this.entityColumnsLabel),
                })
                : rows;
        }

        private buildFileDescription(): { description?: string, error?: boolean } {
            return this.fileDescription;
        }

        private buildRow(row: Record<string, string>): BaseEntityInput {
            let entity: BaseEntityInput;
            try {
                entity = buildInsuredInput(row, this.form);
            } catch (error) {
                // Lists of objects (Producers, collaterals, equipment) are read with JSON.parse, and a
                // producer that is not an object is reported the same way.
                if (error instanceof SyntaxError) {
                    throw new Error(`Could not read the row for "${row.displayName || row.contactEmail}": `
                        + 'a list of objects, such as Producers, is not valid JSON. Each object must be valid '
                        + 'JSON, objects are separated by "|", and a value cannot contain "|".', { cause: error });
                }
                throw error;
            }
            // Coverage types sit inside the producer JSON, which buildValue does not resolve labels in.
            return this.importableProducerCoverageTypes
                ? withResolvedCoverageTypes(entity, getProducerCoverageTypeSchema(
                    this.importableProducerCoverageTypes, this.producerCoverageTypeLabels,
                ))
                : entity;
        }

        private async checkProducers(entities: BaseEntityInput[]): Promise<void> {
            const problems = findProducerProblems(entities);
            if (problems.length > 0) {
                throw new Error(describeProducerProblems(problems));
            }
            const blockedEmails = await this.findBlockedProducerEmails(collectProducerEmails(entities));
            if (blockedEmails.length > 0) {
                throw new Error('These producer emails use a personal or disposable domain: '
                    + `${blockedEmails.join(', ')}. Use business email addresses instead.`);
            }
        }

        /** Whether the file brings producers. Only a Download Data file on a producer-flow account may.
         *
         * A column of a custom property named "Producers" is not the producers column. An export names
         * custom-property columns by key and a worksheet by label, so each is matched that way. */
        private readProducersColumn(header: string[], isExportFile: boolean): boolean {
            const customPropertyNames = isExportFile
                ? getFlattenedCustomPropertyKeys(this.sortedCustomProperties)
                : getFlattenedCustomPropertyNames(this.sortedCustomProperties);
            if (!hasProducersColumn(header, customPropertyNames)) {
                return false;
            }
            if (!this.producerCoverageTypes) {
                throw new Error(PRODUCERS_FLOW_OFF_MESSAGE);
            }
            if (this.hasProducersCustomProperty) {
                throw new Error(PRODUCERS_CUSTOM_PROPERTY_MESSAGE);
            }
            if (!isExportFile) {
                throw new Error(PRODUCERS_WORKSHEET_MESSAGE);
            }
            return true;
        }

        private async readInsuredInput(csv: File): Promise<{
            csvHeaders: string[];
            rows: BaseEntityInput[];
            csvColumnKeys: string[];
            droppedColumns: string[];
        }> {
            // Get raw parsed CSV
            let parsedRows = await readCsv(csv);
            const rawHeader = parsedRows[0] || [];
            const isExportFile = isEntityExportCsv(rawHeader);
            // Checked before the export rewrite, which would otherwise drop the column as read-only.
            this.fileHasProducersColumn = this.readProducersColumn(rawHeader, isExportFile);
            // If this is a "Download Data" export, rewrite it into the canonical import shape.
            let droppedColumns: string[] = [];
            if (isExportFile) {
                this.exportSource = { header: rawHeader, rows: parsedRows.slice(1) };
                // normalizeExportCsv fills absent import columns with empty values. On a
                // non-unique relying party that would import as `id: null` on every row and
                // duplicate the whole file instead of updating it; where contactEmail still
                // addresses the entity those blanks are exactly right.
                if (!this.contactEmailUniquenessEnabled && !rawHeader.map(trim).includes(ENTITY_ID_COLUMN_LABEL)) {
                    throw new Error(LEGACY_CSV_FORMAT_MESSAGE);
                }
                const keyToLabel = zipObject(this.entityColumnsKeys, this.entityColumnsLabel);
                const normalized = normalizeExportCsv(parsedRows, {
                    entityColumnsLabel: this.entityColumnsLabel,
                    keyToLabel,
                    booleanLabels: this.booleanColumnLabels,
                    collateralLabels: this.collateralColumnLabels,
                    equipmentLabels: this.equipmentColumnLabels,
                    listLabels: this.listColumnLabels,
                });
                parsedRows = normalized.rows;
                droppedColumns = normalized.droppedColumns;
            }
            const rows = parsedRows;
            // Extract header from the results list
            const csvHeaders = (rows.shift() || []).slice(0, this.entityColumnsLabel.length).map(trim);
            const csvColumnKeys = csvHeaders.map((label) => this.entityColumnsLabelKeyMap[label]);
            // Check if it's empty
            if (rows.length === 0) {
                throw new Error('Provided CSV file is empty');
            }
            if (rows.some((row) => (row.length !== csvHeaders.length))) {
                throw new Error('The file is not a valid CSV and could not be parsed.');
            }
            // Validate header
            if (csvHeaders.some((label) => !this.entityColumnsLabel.includes(label))) {
                throw new Error('The file has an extra column that is not currently an Insured field.');
            }
            // A non-unique relying party needs the Entity ID column, because nothing else addresses
            // the entity. A file without it is an outdated worksheet, which is worth saying plainly
            // rather than reporting as a generic missing field. Checked after the extra-column rule
            // so a genuinely broken file still reports as broken.
            const hasEntityIdColumn = csvHeaders.includes(ENTITY_ID_COLUMN_LABEL);
            if (!hasEntityIdColumn && !this.contactEmailUniquenessEnabled) {
                throw new Error(LEGACY_CSV_FORMAT_MESSAGE);
            }
            // Entity ID and Producers are the only columns a file may leave out: worksheets saved before
            // Entity ID existed stay importable, and only a Download Data file carries Producers. Rows
            // without an Entity ID match on contactEmail.
            const optionalAbsentLabels = [
                ...(hasEntityIdColumn ? [] : [ ENTITY_ID_COLUMN_LABEL ]),
                ...(csvHeaders.includes(PRODUCERS_COLUMN_LABEL) ? [] : [ PRODUCERS_COLUMN_LABEL ]),
            ];
            const expectedColumnCount = this.entityColumnsLabel
                .filter((label) => !optionalAbsentLabels.includes(label)).length;
            if (
                uniq(csvHeaders).length !== csvHeaders.length ||
                csvHeaders.length !== expectedColumnCount
            ) {
                throw new Error('The file is missing a standard field or Insured field.');
            }
            const objectTypeFields = this.sortedCustomProperties.filter((field) => isObject(field.schema));
            const objectTypeKeys = objectTypeFields.map((field) => field.key);
            const combinedColumnKeys = getCsvColumnKeysWithCombinedObjects(csvColumnKeys, objectTypeKeys);

            const getCombinedRows =
                (row: string[]) => getCsvValuesWithCombinedObjects(csvColumnKeys, row, objectTypeKeys);
            const formattedRows = rows
                .map((row) => row.map(trim))
                .filter((row) => row.some(Boolean))
                .map((row) => zipObject(combinedColumnKeys, getCombinedRows(row)))
                .map((row) => this.buildRow(row));
            return {
                csvHeaders,
                csvColumnKeys,
                rows: formattedRows,
                droppedColumns,
            };
        }
    }
</script>
